/** Pure cents arithmetic shared by server pricing and CRM previews. No runtime dependencies. */
export type ChargeTotalsItem = { unitPriceCents: number; quantity: number };
export type ChargeDiscount = {
  type: 'percent' | 'fixed';
  /** Whole percent (1..100) or whole cents (>0), validated at the request boundary. */
  value: number;
};
export type ChargeTotals = { subtotalCents: number; discountCents: number; totalCents: number };

const PERCENT_BASE = 100;

export function lineTotalCents(item: ChargeTotalsItem): number {
  return item.unitPriceCents * item.quantity;
}

export function computeSubtotalCents(items: readonly ChargeTotalsItem[]): number {
  return items.reduce((total, item) => total + lineTotalCents(item), 0);
}

/** Half cents round the discount up for the customer; both discount types cap at the subtotal. */
export function computeDiscountCents(subtotalCents: number, discount?: ChargeDiscount): number {
  if (!discount) return 0;
  const requested = discount.type === 'percent'
    ? Math.round((subtotalCents * discount.value) / PERCENT_BASE)
    : discount.value;
  return Math.min(subtotalCents, requested);
}

/** Produces fresh totals without changing the priced lines or discount. */
export function computeChargeTotals(
  items: readonly ChargeTotalsItem[],
  discount?: ChargeDiscount,
): ChargeTotals {
  const subtotalCents = computeSubtotalCents(items);
  const discountCents = computeDiscountCents(subtotalCents, discount);
  return { subtotalCents, discountCents, totalCents: subtotalCents - discountCents };
}

/** Remainder cents go to the first shares, so every quote is deterministic and sums to the total. */
export function equalSplitShares(totalCents: number, parts: number): number[] {
  if (!Number.isInteger(parts) || parts < 1) {
    throw new RangeError('equalSplitShares needs a positive whole number of parts.');
  }
  const base = Math.floor(totalCents / parts);
  const remainder = totalCents - base * parts;
  return Array.from({ length: parts }, (_unused, index) => base + (index < remainder ? 1 : 0));
}
